import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import type {
  NodeDetail as NodeDetailData,
  NodePodSummary,
  NodeTelemetry,
  NodeTelemetrySeries,
  Section,
  TrendSeries,
} from "@k8s-dashboard/contracts";
import { drillKeys, useNodeDetail } from "@/api/queries";
import { LineChart } from "@/components/LineChart";
import { Panel, StatTile, StatusBadge } from "@/components/primitives";
import { SectionView } from "@/components/SectionState";
import { VirtualTable } from "@/components/VirtualTable";
import { Breadcrumb, UsageBar, refPath, withSearch } from "@/components/drill";
import { useT } from "@/i18n/I18nProvider";
import { duration, num } from "@/lib/format";
import { useDashboardParams } from "@/state/useDashboardParams";
import { PageError, PageHeader, useDrillControls, useInvalidate } from "../drill/common";
import {
  SERIES_LABEL,
  TELEMETRY_REASON_LABEL,
  TELEMETRY_STATE_LABEL,
  ratioExceeds,
  ratioPercentText,
} from "./nodeDisplay";

/**
 * Node 상세 — 노드 상태·그 노드의 Pod·노드 시계열 (#207)
 * --------------------------------------------------------------------------
 * 세 섹션(node·pods·telemetry)이 **독립**입니다. 메트릭 원천이 죽어도 노드 상태와
 * Pod 목록은 값을 유지합니다. (ADR 0002)
 *
 * 텔레메트리는 네 상태를 **정직하게** 구분해 보여 줍니다 — ok·warming·missing·
 * degraded. "값 없음"을 장애로 보여 주거나 장애를 빈 차트로 보여 주지 않습니다.
 * 특히 warming은 노드가 방금 만들어져 아직 믿을 수 있는 샘플이 없다는 **사실**이며,
 * 서버가 그 구간의 값을 애초에 내려보내지 않습니다(재생성된 노드에 이전 노드의
 * 값이 섞이지 않도록).
 *
 * 신원은 이름이 아니라 이름 + UID입니다. UID가 없으면 조회하지 않습니다.
 */
export function NodeDetail() {
  const { search } = useLocation();
  const [params] = useSearchParams();
  const { name = "" } = useParams();
  const uid = params.get("uid") ?? "";
  const { clusterId, range, from, to, refreshMs } = useDashboardParams();

  const q = useNodeDetail(clusterId, name, uid, range, refreshMs, from, to);
  const invalidate = useInvalidate(drillKeys.node(clusterId, name, uid, range, from, to));
  const { controls } = useDrillControls(invalidate, q.isFetching, q.dataUpdatedAt || undefined);
  const { t } = useT("common");

  const detail = q.data?.node.data;

  return (
    <div className="page">
      <Breadcrumb
        items={[
          { label: "Nodes", to: withSearch("/nodes", search) },
          { label: name },
        ]}
      />
      <PageHeader
        title={name}
        subtitle={detail ? `${clusterId} · ${detail.roles.length ? detail.roles.join(" · ") : "역할 없음"}` : clusterId}
        controls={controls}
      />

      {/* uid 없는 링크는 서버에 보내지 않습니다 — 400을 유발하는 대신 무엇이
          잘못됐는지 화면에서 말합니다. */}
      {!uid ? (
        <section className="panel">
          <div className="panel__body">
            <div className="state state--error">
              <span className="state__glyph" aria-hidden="true">
                !
              </span>
              <span className="state__title">{t("common.node.notFound.title")}</span>
              <span className="state__detail">{t("common.node.uidRequired.detail")}</span>
              <Link to={withSearch("/nodes", search)}>Nodes</Link>
            </div>
          </div>
        </section>
      ) : q.isError ? (
        <PageError error={q.error} onRetry={invalidate} />
      ) : (
        <>
          <Panel title="노드" section={q.data?.node} referenceIso={q.data?.generatedAt}>
            <SectionView
              section={q.data?.node}
              loading={q.isLoading}
              emptyTitle={t("common.node.notFound.title")}
              emptyDetail={t("common.node.notFound.detail")}
            >
              {(node) => <NodeFacts node={node} />}
            </SectionView>
          </Panel>

          <NodeTelemetryPanels
            section={q.data?.telemetry}
            loading={q.isLoading}
            referenceIso={q.data?.generatedAt}
            nodeName={name}
          />

          <Panel title="이 노드의 Pod" section={q.data?.pods} referenceIso={q.data?.generatedAt} flush>
            <SectionView
              section={q.data?.pods}
              loading={q.isLoading}
              emptyTitle="이 노드에 스케줄된 Pod가 없습니다"
              emptyDetail="종료되지 않은 Pod가 없습니다."
            >
              {(pods) => (
                <>
                  <NodePodTable pods={pods.pods} search={search} />
                  {pods.truncation.omitted > 0 ? (
                    <p className="muted" role="note" style={{ padding: "var(--space-4)", margin: 0 }}>
                      {t("common.node.truncated", {
                        omitted: num(pods.truncation.omitted),
                        total: num(pods.truncation.total),
                      })}
                      {pods.bytesTruncated ? ` ${t("common.node.truncatedBytes")}` : ""}
                    </p>
                  ) : null}
                </>
              )}
            </SectionView>
          </Panel>
        </>
      )}
    </div>
  );
}

/** 노드 사실 — 상태·버전·용량·비율·Condition·Taint·선택 라벨입니다. */
function NodeFacts({ node }: { node: NodeDetailData }) {
  const { t } = useT("common");
  const unavailable = t("common.node.ratio.unavailable");
  return (
    <>
      <div className="row row--wrap">
        <StatusBadge severity={node.severity} />
        <span className="muted">kubelet {node.kubeletVersion}</span>
        <span className="muted">{node.osImage}</span>
        <span className="muted">나이 {duration(node.ageSeconds)}</span>
        {node.internalIP ? <span className="muted">{node.internalIP}</span> : null}
        {node.unschedulable ? <span className="muted">스케줄 중지(cordon)</span> : null}
      </div>

      <div className="grid grid--kpi">
        {/* 비율이 null이면 "0%"가 아니라 계산 불가입니다 — 분모(allocatable)가 없습니다. */}
        <StatTile
          label="CPU Request / 할당가능"
          value={ratioPercentText(node.cpuRequestRatio) ?? "—"}
          // 1을 넘는 값은 깎지 않고 경고 톤으로 말합니다 — 초과 할당은 진단 정보입니다.
          tone={ratioExceeds(node.cpuRequestRatio) ? "warning" : undefined}
          footnote={
            node.cpuRequestRatio === null
              ? unavailable
              : `${num(node.requested.cpuMilli)}m / ${num(node.allocatable.cpuMilli)}m`
          }
        />
        <StatTile
          label="Memory Request / 할당가능"
          value={ratioPercentText(node.memoryRequestRatio) ?? "—"}
          tone={ratioExceeds(node.memoryRequestRatio) ? "warning" : undefined}
          footnote={
            node.memoryRequestRatio === null
              ? unavailable
              : `${num(node.requested.memoryMib)} Mi / ${num(node.allocatable.memoryMib)} Mi`
          }
        />
        <StatTile
          label="Pods"
          value={`${num(node.podsTotal)} / ${num(node.allocatable.pods)}`}
          footnote={`CPU Limit 합계 ${num(node.limits.cpuMilli)}m · Memory Limit 합계 ${num(node.limits.memoryMib)} Mi`}
        />
        <StatTile
          label="런타임"
          value={<span className="ds-ident">{node.containerRuntime || "—"}</span>}
          footnote={`${node.operatingSystem || "—"} / ${node.architecture || "—"} · kernel ${node.kernelVersion || "—"}`}
        />
      </div>

      <div className="row row--wrap">
        {/* allocatable은 줄어들 수 있고 이미 스케줄된 Pod는 남으므로 비율이 1을
            넘을 수 있습니다. 깎지 않고 중앙 눈금 100%인 스케일로 그립니다. */}
        <UsageBar ratio={node.cpuRequestRatio} label="CPU Request" max={2} />
        <UsageBar ratio={node.memoryRequestRatio} label="Memory Request" max={2} />
      </div>

      {node.labels?.length ? (
        <div className="row row--wrap">
          {/* 배포가 선언한 정확 키만 나옵니다. 원문 라벨 map은 서버가 내려보내지 않습니다. */}
          {node.labels.map((l) => (
            <span key={l.key} className="muted">
              {l.key}: {l.value}
            </span>
          ))}
        </div>
      ) : null}

      <NodeConditions node={node} />
      <NodeTaints node={node} />
    </>
  );
}

function NodeConditions({ node }: { node: NodeDetailData }) {
  const { t } = useT("common");
  if (node.conditions.length === 0) return null;
  return (
    <>
      <p className="muted" style={{ margin: 0 }}>
        Conditions
      </p>
      <table className="ds-data-table">
        <thead>
          <tr>
            <th>Type</th>
            <th style={{ width: 110 }}>상태</th>
            <th>사유</th>
          </tr>
        </thead>
        <tbody>
          {node.conditions.map((c) => (
            <tr key={c.type}>
              <td className="ds-ident">{c.type}</td>
              <td>
                {/* 상태는 색만으로 전달하지 않습니다 — 배지에 아이콘·텍스트가 함께 있습니다. */}
                <StatusBadge severity={c.severity} small />
              </td>
              <td className="muted">{c.reason || c.message || c.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {node.truncation.conditions.omitted > 0 ? (
        <p className="muted" role="note" style={{ margin: 0 }}>
          {t("common.node.conditionsOmitted", { omitted: num(node.truncation.conditions.omitted) })}
        </p>
      ) : null}
    </>
  );
}

function NodeTaints({ node }: { node: NodeDetailData }) {
  if (node.taints.length === 0) return null;
  return (
    <>
      <p className="muted" style={{ margin: 0 }}>
        Taints
      </p>
      <ul>
        {node.taints.map((taint) => (
          // value는 서버가 내려보내지 않습니다 — 키와 effect만 사실로 보여 줍니다.
          <li key={`${taint.key}:${taint.effect}`} className="muted">
            {taint.key} · {taint.effect}
          </li>
        ))}
      </ul>
    </>
  );
}

/* ── 텔레메트리 ──────────────────────────────────────────────────────────── */

/**
 * CPU와 메모리를 **별도 차트**로 그립니다. 단위가 cores와 bytes로 달라
 * 한 차트에 올리면 이중 y축이 필요해지고, 이중 y축은 금지입니다. (ADR 0001)
 */
function NodeTelemetryPanels({
  section,
  loading,
  referenceIso,
  nodeName,
}: {
  section: Section<NodeTelemetry> | undefined;
  loading: boolean;
  referenceIso?: string;
  nodeName: string;
}) {
  // TrendsGrid와 같은 모양입니다 — 같은 섹션을 두 Panel이 함께 읽고, 각 Panel이
  // 자기 단위의 시리즈만 그립니다. 패널을 중첩하지 않습니다.
  return (
    <div className="grid grid--trends">
      <TelemetryPanel
        titleId="common.node.chart.cpu"
        prefix="cpu."
        section={section}
        loading={loading}
        referenceIso={referenceIso}
        nodeName={nodeName}
      />
      <TelemetryPanel
        titleId="common.node.chart.memory"
        prefix="memory."
        section={section}
        loading={loading}
        referenceIso={referenceIso}
        nodeName={nodeName}
      />
    </div>
  );
}

/**
 * 차트 하나와 그 시리즈들의 상태입니다.
 *
 * 값이 있는 시리즈는 그리고, 값이 없는 시리즈는 **왜 없는지**를 글로 말합니다.
 * 일부만 값이 있는 상태(partial)도 숨기지 않습니다.
 */
function TelemetryPanel({
  titleId,
  prefix,
  section,
  loading,
  referenceIso,
  nodeName,
}: {
  titleId: string;
  prefix: string;
  section: Section<NodeTelemetry> | undefined;
  loading: boolean;
  referenceIso?: string;
  nodeName: string;
}) {
  const { t } = useT("common");
  const step = section?.data?.stepSeconds ?? 0;
  const title = t(titleId);
  return (
    <Panel
      title={title}
      subtitle={step > 0 ? t("common.node.telemetry.step", { seconds: num(step) }) : undefined}
      section={section}
      referenceIso={referenceIso}
    >
      <SectionView
        section={section}
        loading={loading}
        emptyTitle="시계열이 없습니다"
        emptyDetail="이 노드의 메트릭이 수집되지 않았습니다."
      >
        {(telemetry) => {
          const series = telemetry.series.filter((s) => s.key.startsWith(prefix));
          const withValues = series.filter((s) => s.state === "ok" && s.points.length > 0);
          const withoutValues = series.filter((s) => !(s.state === "ok" && s.points.length > 0));
          const partial = withValues.length > 0 && withoutValues.length > 0;
          return (
            <>
              {withValues.length > 0 ? (
                <LineChart
                  series={withValues.map((sr) => toTrendSeries(sr, t(SERIES_LABEL[sr.key])))}
                  stepSeconds={telemetry.stepSeconds}
                  ariaLabel={`${nodeName} ${title} · ${withValues.map((sr) => t(SERIES_LABEL[sr.key])).join(", ")}`}
                />
              ) : null}
              {partial ? (
                <p className="muted" role="note" style={{ margin: 0 }}>
                  {t("common.node.telemetry.partial")}
                </p>
              ) : null}
              {withoutValues.map((s) => (
                <TelemetrySeriesState key={s.key} series={s} />
              ))}
            </>
          );
        }}
      </SectionView>
    </Panel>
  );
}

/**
 * 상태를 색이 아니라 **텍스트 라벨 + 사유**로 말합니다.
 *
 * 문구는 전부 i18n입니다 — 서버는 닫힌 코드만 보내므로 en 모드에서 서버 언어가
 * 새어 나올 자리가 없습니다.
 */
function TelemetrySeriesState({ series }: { series: NodeTelemetrySeries }) {
  const { t } = useT("common");
  const stateLabel = t(TELEMETRY_STATE_LABEL[series.state]);
  const reasonId = series.reasonCode ? TELEMETRY_REASON_LABEL[series.reasonCode] : undefined;
  return (
    <div className="state" role="note">
      <span className="state__title">
        {t(SERIES_LABEL[series.key])} · {stateLabel}
      </span>
      {reasonId ? <span className="state__detail">{t(reasonId)}</span> : null}
      {series.state === "warming" && series.readyAt ? (
        <span className="state__detail">{t("common.node.telemetry.readyAt", { readyAt: series.readyAt })}</span>
      ) : null}
    </div>
  );
}

/** LineChart 계약으로 바꿉니다. 표시 이름은 키에서 번역해 넣습니다. */
function toTrendSeries(sr: NodeTelemetrySeries, label: string): TrendSeries {
  return { key: sr.key, label, unit: sr.unit, points: sr.points };
}

/* ── Pod 목록 ───────────────────────────────────────────────────────────── */

const POD_COLS = ["auto", "auto", "110px", "90px", "130px", "140px"];

/**
 * 이 노드의 Pod 표. 서버 상한이 500이므로 **가상 스크롤**입니다 — 서버가 개수를
 * 자르는 것과 DOM 행 수를 묶는 것은 다른 문제입니다.
 */
function NodePodTable({ pods, search }: { pods: NodePodSummary[]; search: string }) {
  return (
    <VirtualTable
      items={pods}
      rowHeight={36}
      height={420}
      columns={POD_COLS}
      // Pod의 신원도 UID입니다.
      getKey={(p) => p.uid}
      header={
        <tr>
          <th>Pod</th>
          <th>Namespace</th>
          <th style={{ width: POD_COLS[2] }}>상태</th>
          <th className="ds-num" style={{ width: POD_COLS[3] }}>
            재시작
          </th>
          <th className="ds-num" style={{ width: POD_COLS[4] }}>
            CPU Request
          </th>
          <th className="ds-num" style={{ width: POD_COLS[5] }}>
            Memory Request
          </th>
        </tr>
      }
      renderRow={(p) => (
        <>
          <td className="ds-ident">
            <Link to={refPath({ podName: p.name, namespace: p.namespace, podUid: p.uid }, search)}>{p.name}</Link>
          </td>
          <td className="muted">{p.namespace}</td>
          <td style={{ width: POD_COLS[2] }}>
            <StatusBadge severity={p.severity} small />
          </td>
          <td className="ds-num" style={{ width: POD_COLS[3] }}>
            {num(p.restarts)}
          </td>
          <td className="ds-num" style={{ width: POD_COLS[4] }}>
            {num(p.cpuRequestMilli)}m
          </td>
          <td className="ds-num" style={{ width: POD_COLS[5] }}>
            {num(p.memoryRequestMib)} Mi
          </td>
        </>
      )}
    />
  );
}
